import React from 'react';
import { useSpin } from '../../context/SpinContext';
import DropZone from './DropZone';
import { Slider } from '../ui/slider';
import { ToggleGroup, ToggleGroupItem } from '../ui/toggle-group';
import { Switch } from '../ui/switch';
import { Input } from '../ui/input';
import { VINYL_PRESETS, VINYL_FINISHES } from '../../lib/vinylPresets';
import RpmSlider from './RpmSlider';

const SHAPES = [
  { id: 'circle', label: 'Circle', style: { borderRadius: '50%' } },
  { id: 'square', label: 'Square', style: { borderRadius: '2px' } },
  { id: 'roundedSquare', label: 'Rounded', style: { borderRadius: '28%' } },
  { id: 'hexagon', label: 'Hexagon', style: { clipPath: 'polygon(25% 5%,75% 5%,100% 50%,75% 95%,25% 95%,0% 50%)' } },
  { id: 'star', label: 'Star', style: { clipPath: 'polygon(50% 0%,61% 35%,98% 35%,68% 57%,79% 91%,50% 70%,21% 91%,32% 57%,2% 35%,39% 35%)' } },
];

const SIZES = [
  { id: 'CD', label: 'CD' },
  { id: '7in', label: '7"' },
  { id: '10in', label: '10"' },
  { id: '12in', label: '12"' },
  { id: '16in', label: '16"' },
];

function ShapeSwatch({ style }) {
  return <div className="w-6 h-6 bg-neutral-400" style={style} />;
}

export default function DiscControls() {
  const { disc, updateDisc, updateVinyl, updateCenterHole, uploadDiscImage, clearDiscImage, errors } = useSpin();

  return (
    <div className="space-y-5">
      <DropZone
        accept="image/*"
        label="Disc / label art (required)"
        hint="PNG or JPG — a square image works best"
        onFile={uploadDiscImage}
        previewSrc={disc.imageUrl}
        fileName={disc.fileName}
        onClear={clearDiscImage}
        error={errors.disc}
      />

      <div>
        <label className="block text-xs font-medium text-neutral-600 mb-1.5">Disc design</label>
        <ToggleGroup
          type="single"
          value={disc.designMode}
          onValueChange={(v) => v && updateDisc({ designMode: v })}
          className="grid grid-cols-2 gap-2 w-full"
        >
          <ToggleGroupItem value="shape" className="flex-1 border border-neutral-200 rounded-lg data-[state=on]:bg-orange-50 data-[state=on]:border-orange-300 data-[state=on]:text-orange-700">Shape</ToggleGroupItem>
          <ToggleGroupItem value="vinyl" className="flex-1 border border-neutral-200 rounded-lg data-[state=on]:bg-orange-50 data-[state=on]:border-orange-300 data-[state=on]:text-orange-700">Vinyl</ToggleGroupItem>
        </ToggleGroup>
      </div>

      {disc.designMode === 'shape' && (
        <div>
          <label className="block text-xs font-medium text-neutral-600 mb-2">Mask shape</label>
          <div className="grid grid-cols-5 gap-2">
            {SHAPES.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => updateDisc({ shape: s.id })}
                title={s.label}
                className={`flex flex-col items-center gap-1.5 py-2.5 rounded-lg border transition-colors ${disc.shape === s.id ? 'border-orange-300 bg-orange-50' : 'border-neutral-200 hover:bg-neutral-50'}`}
              >
                <ShapeSwatch style={s.style} />
              </button>
            ))}
          </div>
        </div>
      )}

      {disc.designMode === 'vinyl' && (
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-neutral-600 mb-2">Finish</label>
            <ToggleGroup
              type="single"
              value={disc.vinyl.finish}
              onValueChange={(v) => v && updateVinyl({ finish: v })}
              className="grid grid-cols-3 gap-2 w-full"
            >
              {VINYL_FINISHES.map((f) => (
                <ToggleGroupItem key={f.id} value={f.id} className="border border-neutral-200 rounded-lg data-[state=on]:bg-orange-50 data-[state=on]:border-orange-300 data-[state=on]:text-orange-700 text-xs">
                  {f.label}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </div>

          <div>
            <label className="block text-xs font-medium text-neutral-600 mb-2">Vinyl color</label>
            <div className="flex items-center gap-2 mb-2.5">
              <input
                type="color"
                value={disc.vinyl.color}
                onChange={(e) => updateVinyl({ color: e.target.value })}
                className="w-9 h-9 rounded-lg border border-neutral-200 cursor-pointer p-0"
              />
              <Input
                value={disc.vinyl.color}
                onChange={(e) => updateVinyl({ color: e.target.value })}
                className="h-9 text-sm font-mono"
              />
            </div>
            <div className="grid grid-cols-8 gap-1.5">
              {VINYL_PRESETS.map((p) => (
                <button
                  key={p.name}
                  type="button"
                  title={p.name}
                  onClick={() => updateVinyl({ color: p.hex })}
                  style={{ backgroundColor: p.hex }}
                  className={`w-full aspect-square rounded-md border-2 transition-transform hover:scale-110 ${disc.vinyl.color === p.hex ? 'border-orange-500' : 'border-white/40'}`}
                />
              ))}
            </div>
          </div>

          {disc.vinyl.finish !== 'solid' && (
            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1.5">
                {disc.vinyl.finish === 'splatter' ? 'Splatter color' : 'Marble vein color'}
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={disc.vinyl.secondaryColor}
                  onChange={(e) => updateVinyl({ secondaryColor: e.target.value })}
                  className="w-9 h-9 rounded-lg border border-neutral-200 cursor-pointer p-0"
                />
                <Input
                  value={disc.vinyl.secondaryColor}
                  onChange={(e) => updateVinyl({ secondaryColor: e.target.value })}
                  className="h-9 text-sm font-mono"
                />
              </div>
            </div>
          )}

          <div className="flex items-center justify-between pt-1">
            <span className="text-xs font-medium text-neutral-600">Show center spindle</span>
            <Switch checked={disc.spindleVisible} onCheckedChange={(v) => updateDisc({ spindleVisible: v })} />
          </div>
        </div>
      )}

      <div className="space-y-3 pt-1 border-t border-neutral-100" data-testid="center-hole-section">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-neutral-600">Center hole (like a wax record)</span>
          <Switch
            checked={disc.centerHole.enabled}
            onCheckedChange={(v) => updateCenterHole({ enabled: v })}
            data-testid="center-hole-toggle"
          />
        </div>
        {disc.centerHole.enabled && (
          <>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs text-neutral-500">Hole size</label>
                <span className="text-xs text-neutral-400 font-mono">{disc.centerHole.sizePct}%</span>
              </div>
              <Slider
                min={3}
                max={14}
                step={1}
                value={[disc.centerHole.sizePct]}
                onValueChange={(v) => updateCenterHole({ sizePct: v[0] })}
                data-testid="center-hole-size-slider"
              />
            </div>
            <div>
              <label className="block text-xs text-neutral-500 mb-2">Hole fill</label>
              <ToggleGroup
                type="single"
                value={disc.centerHole.fill}
                onValueChange={(v) => v && updateCenterHole({ fill: v })}
                className="grid grid-cols-2 gap-2 w-full"
              >
                <ToggleGroupItem value="transparent" className="border border-neutral-200 rounded-lg data-[state=on]:bg-orange-50 data-[state=on]:border-orange-300 data-[state=on]:text-orange-700 text-xs" data-testid="center-hole-fill-transparent">
                  Show background
                </ToggleGroupItem>
                <ToggleGroupItem value="color" className="border border-neutral-200 rounded-lg data-[state=on]:bg-orange-50 data-[state=on]:border-orange-300 data-[state=on]:text-orange-700 text-xs" data-testid="center-hole-fill-color">
                  Custom color
                </ToggleGroupItem>
              </ToggleGroup>
            </div>
            {disc.centerHole.fill === 'color' && (
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={disc.centerHole.color}
                  onChange={(e) => updateCenterHole({ color: e.target.value })}
                  className="w-9 h-9 rounded-lg border border-neutral-200 cursor-pointer p-0"
                  data-testid="center-hole-color-picker"
                />
                <Input
                  value={disc.centerHole.color}
                  onChange={(e) => updateCenterHole({ color: e.target.value })}
                  className="h-9 text-sm font-mono"
                  data-testid="center-hole-color-input"
                />
              </div>
            )}
          </>
        )}
      </div>

      <div>
        <label className="block text-xs font-medium text-neutral-600 mb-2">Disc size</label>
        <ToggleGroup
          type="single"
          value={disc.size}
          onValueChange={(v) => v && updateDisc({ size: v })}
          className="grid grid-cols-5 gap-1.5 w-full"
        >
          {SIZES.map((s) => (
            <ToggleGroupItem key={s.id} value={s.id} className="border border-neutral-200 rounded-lg text-xs data-[state=on]:bg-orange-50 data-[state=on]:border-orange-300 data-[state=on]:text-orange-700">{s.label}</ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>

      <RpmSlider rpm={disc.rpm} onChange={(rpm) => updateDisc({ rpm })} />

      <div className="flex items-center justify-between pt-1">
        <span className="text-xs font-medium text-neutral-600">Reverse spin</span>
        <Switch
          checked={disc.reverse}
          onCheckedChange={(v) => updateDisc({ reverse: v })}
          data-testid="reverse-spin-toggle"
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-xs font-medium text-neutral-600">Zoom</label>
          <span className="text-xs text-neutral-400 font-mono">{disc.zoom.toFixed(1)}x</span>
        </div>
        <Slider
          min={1}
          max={4}
          step={0.1}
          value={[disc.zoom]}
          onValueChange={(v) => updateDisc({ zoom: v[0] })}
        />
      </div>
    </div>
  );
}
